import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';
import { Upload, Camera, Trash2, Link as LinkIcon, Loader2, AlertCircle, X } from 'lucide-react';

export default function CentrelinkVerification({ 
  attachmentKey, 
  attachmentNameKey, 
  data, 
  onChange 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Custom Webcam/Camera State
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);

  const fileUrl = data[attachmentKey];
  const fileName = data[attachmentNameKey];

  const handleFileUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      onChange(attachmentKey, file_url);
      onChange(attachmentNameKey, file.name);
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = () => {
    onChange(attachmentKey, "");
    onChange(attachmentNameKey, "");
    setError(null);
  };

  // Webcam operations
  const startCamera = async () => {
    setShowCamera(true);
    setCameraError(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Webcam access not supported on this device/browser.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing webcam:", err);
      setCameraError(err.message || "Could not access the camera. Falling back to default browser input.");
      // Fallback: trigger default mobile file camera capture
      setTimeout(() => {
        cameraInputRef.current?.click();
        setShowCamera(false);
      }, 1500);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      
      const ctx = canvas.getContext('2d');
      // Flip horizontally if using front/user camera (optional, but since we specify environment we don't flip)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setError("Failed to generate capture image.");
          return;
        }
        const file = new File([blob], `centrelink_capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
        stopCamera();
        await handleFileUpload([file]);
      }, 'image/jpeg', 0.85);
    } catch (err) {
      console.error("Failed to capture image:", err);
      setError("Failed to capture photo from webcam.");
      stopCamera();
    }
  };

  // Stop camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  return (
    <div className="space-y-4">
      {/* 1. Main Toggle / Link */}
      <div>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="text-sm font-semibold text-blue-700 underline hover:text-blue-900 block focus:outline-none"
        >
          Confirm Centrelink payment
        </button>
      </div>

      {/* 2. Collapsible content box */}
      {isOpen && (
        <div className="relative p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4 transition-all duration-200">
          {/* Close button X in top right */}
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="absolute top-2.5 right-2.5 p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-slate-600 transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="space-y-1.5 pr-6">
            <div>
              <a
                href="https://business.centrelink.gov.au/LoginServices/source/VANguard/ValidateBAN.jsp?finalURL=http%3A%2F%2Fbusiness.humanservices.gwy%2FLoginServices%2FAuthenticate.do"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-700 hover:underline flex items-center gap-1.5"
              >
                Confirm centrelink payment via Business Online
                <span className="text-xs text-gray-500 font-normal">(Logon link)</span>
              </a>
            </div>
            <div>
              <span className="text-xs text-slate-500 block">
                If you need to apply for Centrelink online portal please use this{' '}
                <a
                  href="https://www.servicesaustralia.gov.au/how-to-apply-to-use-centrelink-confirmation-eservices-for-businesses?context=23236"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-blue-700 underline hover:text-blue-900"
                >
                  link
                </a>
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs font-semibold text-gray-700 block">Or upload proof manually:</Label>
            
            <div className="flex flex-wrap gap-2.5">
              {/* Hidden Inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
              />
              {/* Default capture input fallback */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
              />

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 bg-white border-gray-300 hover:bg-gray-50 font-medium"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="w-4 h-4 text-gray-500" />
                Upload
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 bg-white border-gray-300 hover:bg-gray-50 font-medium"
                onClick={startCamera}
                disabled={uploading}
              >
                <Camera className="w-4 h-4 text-gray-500" />
                Take photo
              </Button>
            </div>
            
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin animate-duration-1000" />
                Uploading document...
              </div>
            )}

            {error && (
              <div className="flex items-center gap-1.5 text-xs text-red-600">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3. Custom Webcam Overlay Modal */}
      {showCamera && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black/85 backdrop-blur-md p-4 transition-all duration-300">
          <div className="relative w-full max-w-lg bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-800">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-950 text-white">
              <span className="text-sm font-semibold tracking-wide">Webcam Capture</span>
              <button 
                type="button" 
                onClick={stopCamera} 
                className="p-1 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Video Viewport */}
            <div className="relative aspect-video bg-black flex items-center justify-center">
              {cameraError ? (
                <div className="p-6 text-center space-y-2">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto" />
                  <p className="text-sm text-slate-300 font-medium">{cameraError}</p>
                </div>
              ) : (
                <>
                  <video 
                    ref={videoRef}
                    autoPlay 
                    playsInline 
                    className="w-full h-full object-cover"
                  />
                  {/* Subtle target frame overlay */}
                  <div className="absolute inset-8 border border-dashed border-white/40 rounded-lg pointer-events-none flex items-center justify-center">
                    <span className="text-[10px] text-white/50 bg-black/40 px-2 py-0.5 rounded font-mono uppercase tracking-widest">
                      Position card here
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Controls */}
            {!cameraError && (
              <div className="flex items-center justify-center gap-4 py-4 bg-slate-950">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={stopCamera} 
                  className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  onClick={capturePhoto}
                  className="h-14 w-14 rounded-full bg-white border-4 border-slate-800 flex items-center justify-center active:scale-95 transition-transform hover:bg-slate-100 hover:border-slate-700 focus:outline-none"
                  title="Capture Photo"
                >
                  <div className="h-6 w-6 rounded-full bg-red-600 animate-pulse" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 4. File link & Attached note */}
      {fileUrl && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-green-800 hover:underline flex items-center gap-1.5"
            >
              centrelink confirmation attached to this application
              <LinkIcon className="w-3.5 h-3.5" />
            </a>
            {fileName && (
              <span className="text-xs text-green-600 truncate max-w-[150px] sm:max-w-xs font-normal">
                ({fileName})
              </span>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={removeAttachment}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
